import React, { lazy, Suspense, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { I18nextProvider } from 'react-i18next';
import { HelmetProvider } from 'react-helmet-async';
import i18n from './i18n';
import theme from './styles/theme';
import { AuthProvider } from './contexts/AuthContext';
import UnsavedChangesProvider from './components/common/UnsavedChangesProvider';
import PublicLayout from './layouts/PublicLayout';
import ERPLayout from './layouts/ERPLayout';
import HomePage from './pages/public/HomePage';
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { usePageTracking } from './hooks/usePageTracking';
import { GoogleReCaptchaProvider } from 'react-google-recaptcha-v3';
import RequireAccess from './components/auth/RequireAccess';
import { ROLES } from './constants/roles';

const LoginModal = lazy(() => import('./components/auth/LoginModal'));
const FeaturesPage = lazy(() => import('./pages/public/FeaturesPage'));
const ContactPage = lazy(() => import('./pages/public/ContactPage'));
const PricingPage = lazy(() => import('./pages/public/PricingPage'));
const TenancyRegisterPage = lazy(() => import('./pages/public/TenancyRegisterPage'));
const TenancyVerifyPage = lazy(() => import('./pages/public/TenancyVerifyPage'));
const TenancyPhoneVerifyPage = lazy(() => import('./pages/public/TenancyPhoneVerifyPage'));
const PrivacyPage = lazy(() => import('./pages/public/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/public/TermsPage'));
const InventoryPage = lazy(() => import('./pages/InventoryPage'));
const ProductDetailPage = lazy(() => import('./pages/ProductDetailPage'));
const PetsPage = lazy(() => import('./pages/PetsPage'));
const SalesPage = lazy(() => import('./pages/SalesPage'));
const InvoicesPage = lazy(() => import('./pages/InvoicesPage'));
const DaycarePage = lazy(() => import('./pages/DaycarePage'));
const GroomingPage = lazy(() => import('./pages/GroomingPage'));
const AdminPage = lazy(() => import('./pages/AdminPage'));
const ContactsAdminPage = lazy(() => import('./pages/ContactsAdminPage'));
const PlatformTenantsPage = lazy(() => import('./pages/PlatformTenantsPage'));
const EmbedPage = lazy(() => import('./pages/embed/EmbedPage'));
const WidgetSubmissionsPage = lazy(() => import('./pages/WidgetSubmissionsPage'));
const OrdersPage = lazy(() => import('./pages/OrdersPage'));
const StorefrontCustomersPage = lazy(() => import('./pages/StorefrontCustomersPage'));

function RouteChangeTracker() {
  usePageTracking();
  return null;
}

function RouteLoadingFallback() {
  const styles = {
    shell: {
      minHeight: '100vh',
      display: 'grid',
      placeItems: 'center',
      padding: '24px',
      background: 'linear-gradient(135deg, #f7fafc 0%, #e8f1f7 55%, #ddebf6 100%)',
    },
    card: {
      width: '100%',
      maxWidth: '520px',
      borderRadius: '16px',
      backgroundColor: '#ffffff',
      border: '1px solid rgba(13, 26, 40, 0.08)',
      boxShadow: '0 16px 48px rgba(13, 26, 40, 0.12)',
      padding: '24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
    },
    title: {
      margin: 0,
      fontSize: theme.typography.fontSize.xl,
      color: '#0d1a28',
    },
    subtitle: {
      margin: 0,
      color: '#4a5d73',
      fontSize: theme.typography.fontSize.sm,
      lineHeight: 1.6,
    },
    bar: {
      width: '100%',
      height: '10px',
      borderRadius: '999px',
      background: 'linear-gradient(90deg, rgba(32, 86, 135, 0.22) 0%, rgba(32, 86, 135, 0.55) 50%, rgba(32, 86, 135, 0.22) 100%)',
      backgroundSize: '200% 100%',
      animation: 'routeLoadingWave 1.4s ease-in-out infinite',
    },
  };

  return (
    <div style={styles.shell}>
      <style>
        {`@keyframes routeLoadingWave {
            0% { background-position: 200% 0; }
            100% { background-position: -200% 0; }
          }`}
      </style>
      <div style={styles.card} role="status" aria-live="polite" aria-label="Loading page">
        <h2 style={styles.title}>Loading your page</h2>
        <p style={styles.subtitle}>Fetching modules and preparing the interface...</p>
        <div style={styles.bar} />
      </div>
    </div>
  );
}

function App() {
  const [isLoginModalOpen, setIsLoginModalOpen] = useState(false);
  const isPrerender = typeof navigator !== 'undefined' && navigator.userAgent === 'ReactSnap';

  const recaptchaSiteKey =
    window.WConfiguration?.RECAPTCHA_SITE_KEY || '6LdWrQAtAAAAAK13omfSOysa-p_yXkvBtr_KaMqp';

  const handleLoginClick = () => {
    setIsLoginModalOpen(true);
  };
  const handleCloseModal = () => setIsLoginModalOpen(false);

  return (
    <I18nextProvider i18n={i18n}>
      <HelmetProvider>
        <AuthProvider>
          <Router>
            <UnsavedChangesProvider>
            <RouteChangeTracker />
            <ToastContainer
              position="top-right"
              autoClose={4000}
              hideProgressBar={false}
              newestOnTop
              closeOnClick
              pauseOnHover
              draggable
              theme="colored"
            />
            {isPrerender ? (
              <>
                <Suspense fallback={null}>
                  <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseModal} />
                </Suspense>

                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route element={<PublicLayout onLoginClick={handleLoginClick} />}>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/features" element={<FeaturesPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/register/start" element={<TenancyRegisterPage />} />
                      <Route path="/register/verify" element={<TenancyVerifyPage />} />
                      <Route path="/register/verify-phone" element={<TenancyPhoneVerifyPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                    </Route>

                    {/* Embeddable widget — no layout, no auth */}
                    <Route path="/embed/:tenantSlug" element={<EmbedPage />} />

                    {/* ERP routes — auth-guarded inside ERPLayout */}
                    <Route path="/app" element={<ERPLayout onLoginClick={handleLoginClick} />}>
                      <Route index element={<Navigate to="/app/inventory" replace />} />
                      <Route path="inventory" element={<InventoryPage />} />
                      <Route path="inventory/:productId" element={<ProductDetailPage />} />
                      <Route path="pets" element={<PetsPage />} />
                      <Route path="sales" element={<SalesPage />} />
                      <Route path="invoices" element={<InvoicesPage />} />
                      <Route path="daycare" element={<DaycarePage />} />
                      <Route path="grooming" element={<GroomingPage />} />
                      <Route
                        path="admin"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <AdminPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="contacts"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <ContactsAdminPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="platform"
                        element={(
                          <RequireAccess minimumRole={ROLES.SUPER_ADMIN}>
                            <PlatformTenantsPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="widget"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <WidgetSubmissionsPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="orders"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <OrdersPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="storefront-customers"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <StorefrontCustomersPage />
                          </RequireAccess>
                        )}
                      />
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                  </Routes>
                </Suspense>
              </>
            ) : (
              <GoogleReCaptchaProvider
                reCaptchaKey={recaptchaSiteKey}
                scriptProps={{ async: true, defer: true, appendTo: 'body' }}
              >
                <Suspense fallback={null}>
                  <LoginModal isOpen={isLoginModalOpen} onClose={handleCloseModal} />
                </Suspense>

                <Suspense fallback={<RouteLoadingFallback />}>
                  <Routes>
                    {/* Public routes */}
                    <Route element={<PublicLayout onLoginClick={handleLoginClick} />}>
                      <Route path="/" element={<HomePage />} />
                      <Route path="/features" element={<FeaturesPage />} />
                      <Route path="/contact" element={<ContactPage />} />
                      <Route path="/pricing" element={<PricingPage />} />
                      <Route path="/register/start" element={<TenancyRegisterPage />} />
                      <Route path="/register/verify" element={<TenancyVerifyPage />} />
                      <Route path="/register/verify-phone" element={<TenancyPhoneVerifyPage />} />
                      <Route path="/privacy" element={<PrivacyPage />} />
                      <Route path="/terms" element={<TermsPage />} />
                    </Route>

                    {/* ERP routes — auth-guarded inside ERPLayout */}
                    <Route path="/app" element={<ERPLayout onLoginClick={handleLoginClick} />}>
                      <Route index element={<Navigate to="/app/inventory" replace />} />
                      <Route path="inventory" element={<InventoryPage />} />
                      <Route path="inventory/:productId" element={<ProductDetailPage />} />
                      <Route path="pets" element={<PetsPage />} />
                      <Route path="sales" element={<SalesPage />} />
                      <Route path="invoices" element={<InvoicesPage />} />
                      <Route path="daycare" element={<DaycarePage />} />
                      <Route path="grooming" element={<GroomingPage />} />
                      <Route
                        path="admin"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <AdminPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="contacts"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <ContactsAdminPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="platform"
                        element={(
                          <RequireAccess minimumRole={ROLES.SUPER_ADMIN}>
                            <PlatformTenantsPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="orders"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <OrdersPage />
                          </RequireAccess>
                        )}
                      />
                      <Route
                        path="storefront-customers"
                        element={(
                          <RequireAccess minimumRole={ROLES.ADMIN}>
                            <StorefrontCustomersPage />
                          </RequireAccess>
                        )}
                      />
                    </Route>

                    {/* Fallback */}
                    <Route path="*" element={<Navigate to="/" replace />} />
                    <Route path="/embed/:tenantSlug" element={<EmbedPage />} />
                  </Routes>
                </Suspense>
              </GoogleReCaptchaProvider>
            )}
            </UnsavedChangesProvider>
          </Router>
        </AuthProvider>
      </HelmetProvider>
    </I18nextProvider>
  );
}

export default App;
